import React, { ReactNode, memo, useMemo, forwardRef } from 'react';
import { DOMProps, HoverEvents, PressEvent } from '@react-types/shared';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { Cell, CellProps } from '../Cell';
import type { TextSize } from '../Text/types';
import './HeaderCell.css';
import { textSkinSizes } from '../Text/utils';
import { ColorlessIconProps, IconArrowDownBig, IconArrowUpBig, IconSwapVertical } from '../../icons';
import { HStack, VStack } from '../Stack';
import { StatefulTooltip, StatefulTooltipProps } from '../StatefulTooltip';
import { useVerticalAlign } from '../../hooks/useVerticalAlign/useVerticalAlign';
import { TooltipProps } from '../Tooltip';
import { CellSizeContext } from '../Table/context';
import { HelpProps } from '../Help';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';

const cls = cn('HeaderCell');

type SortDirection = 'asc' | 'desc' | false;

export interface HeaderCellProps
    extends TestProps,
        HoverEvents,
        DOMProps,
        Omit<CellProps, 'children' | 'statusColor' | 'type'> {
    /**
     * Названия классов для элемента
     */
    className?: string;
    /**
     * Основной текст ячейки - строка или компонент
     */
    title: ReactNode;
    /**
     * Направление сортировки
     */
    sortDirection?: SortDirection;
    /**
     * Дефолтное направление первой сортировки
     * @default 'asc'
     */
    firstSortDirection?: Exclude<SortDirection, false>;
    /**
     * Обработчик направления сортировки
     * Обработчик будет работать только в том случае, если в компоненте присутствует параметр sortDirection
     */
    onSort?: (direction: SortDirection) => void;
    /**
     * Контент тултипа
     */
    tooltipContent?: ReactNode;
    /**
     * Максимальная ширина тултипа
     * @default auto
     */
    tooltipMaxWidth?: TooltipProps['maxWidth'];
    /**
     * Кастомный контент тултипа
     * Заменяет стандартное отображение контента тултипа (на базе tooltipContent)
     */
    tooltipCustomContent?: TooltipProps['customContent'];
    /**
     * Задержка, после которой закрывается тултип
     * @default 0
     */
    tooltipDelay?: StatefulTooltipProps['delay'];
    /**
     * Задержка, после которой открывается тултип
     * @default 0
     */
    tooltipOpenDelay?: StatefulTooltipProps['openDelay'];
    /**
     * Параметры автофокусировки над содержимым тултипа
     */
    tooltipAutoFocusProps?: StatefulTooltipProps['autoFocusProps'];
    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с текстом рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;
    /**
     * Обрезать ли текст заголовка
     * @default true
     */
    isTitleTruncated?: boolean;
    /**
     * Максимальное количество строк, доступных к прочтению без обрезания контента
     */
    maxLines?: number;
    /** Аддон под контентом */
    addonBottom?: ReactNode;
    /**
     * Слот под фильтрацию который находится справа от children и иконки
     */
    filterAddon?: ReactNode;
}

/**
 * Описание размеров для размера ячейки
 */
type HeaderCellSize = {
    /**
     * Размер основного текста
     */
    titleSize: TextSize;
};

type HeaderCellSizeConfigProps = Required<HeaderCellProps>['size'];

const HeaderCellSizeConfig: Record<HeaderCellSizeConfigProps, HeaderCellSize> = {
    s: {
        titleSize: 'xs',
    },
    m: {
        titleSize: 's',
    },
    l: {
        titleSize: 'mc',
    },
};

const DEFAULT_SORT_DIRECTION_ORDER = [false, 'asc', 'desc'] as const;

const defaultAutoFocusProps = {};

/**
 * Компонент для отображения заголовка таблицы
 */
export const HeaderCell = memo(
    forwardRef<HTMLDivElement, HeaderCellProps>(function HeaderCell(props, ref) {
        const {
            className,
            title,
            onClick,
            onSort,
            sortDirection,
            firstSortDirection = 'asc',
            tooltipContent,
            tooltipMaxWidth,
            tooltipOpenDelay = 0,
            tooltipDelay = 0,
            tooltipCustomContent,
            help,
            onHoverStart,
            onHoverEnd,
            addonBottom,
            tooltipAutoFocusProps = defaultAutoFocusProps,
            maxLines,
            filterAddon,
            ...cellProps
        } = props;

        const tableSize = React.useContext(CellSizeContext);
        const { isTitleTruncated = true, align, size = 'm', 'data-testid': dateTestId = 'HeaderCell' } = cellProps;

        const cellSize = tableSize ?? size;
        const { titleSize } = HeaderCellSizeConfig[cellSize];

        const role = sortDirection ? 'main' : 'supplementary';

        const { addonClasses } = useVerticalAlign({
            component: 'text',
            size: titleSize,
            addonSize: '12',
        });

        const onClickHandler = useMemo(() => {
            if (!onClick && !onSort) {
                return;
            }

            return (e: PressEvent) => {
                onClick?.(e);

                if (sortDirection === undefined) {
                    return;
                }

                const sortDirectionOrder = [...DEFAULT_SORT_DIRECTION_ORDER];
                if (firstSortDirection !== 'asc') {
                    sortDirectionOrder.reverse();
                }

                const currentSortDirectionIndex = sortDirectionOrder.indexOf(sortDirection);
                const nextSortDirectionIndex = (currentSortDirectionIndex + 1) % sortDirectionOrder.length;

                const nextSortDirection = sortDirectionOrder[nextSortDirectionIndex];
                if (nextSortDirection === undefined) {
                    return;
                }

                onSort?.(nextSortDirection);
            };
        }, [firstSortDirection, onClick, onSort, sortDirection]);

        let icon;
        const iconProps = useMemo<ColorlessIconProps>(
            () => ({
                size: '12',
                className: cls(
                    'sortIcon',
                    {
                        size: cellSize,
                        sorted: Boolean(sortDirection),
                        withIconRight: Boolean(cellProps.iconRight),
                        withFilterAddon: Boolean(filterAddon),
                    },
                    [addonClasses],
                ),
                role,
            }),
            [cellSize, sortDirection, cellProps.iconRight, filterAddon, addonClasses, role],
        );

        if (sortDirection === 'desc') {
            icon = <IconArrowDownBig {...iconProps} data-testid={'IconSort.desc'} />;
        } else if (sortDirection === 'asc') {
            icon = <IconArrowUpBig {...iconProps} data-testid={'IconSort.asc'} />;
        } else {
            icon = <IconSwapVertical {...iconProps} />;
        }

        return (
            <StatefulTooltip
                openDelay={tooltipOpenDelay}
                delay={tooltipDelay}
                title={tooltipContent}
                trigger="hover"
                hasClose={false}
                placement="bottom"
                color="inverted"
                maxWidth={tooltipMaxWidth}
                customContent={tooltipCustomContent}
                autoFocusProps={tooltipAutoFocusProps}
            >
                <Cell
                    className={cls({ sortable: sortDirection === false, sorted: Boolean(sortDirection) }, [className])}
                    onClick={onClickHandler}
                    onHoverStart={onHoverStart}
                    onHoverEnd={onHoverEnd}
                    {...cellProps}
                    data-testid={dateTestId}
                    size={cellSize}
                    ref={ref}
                    addonBottom={addonBottom}
                >
                    <VStack alignItems="flex-start" className={cls('container')}>
                        <HStack className={cls('text')}>
                            <TextGenericWithSkin
                                isTruncated={isTitleTruncated}
                                maxLines={maxLines}
                                role={role}
                                color="current"
                                textAlign={align}
                                textSize="current-main"
                                skinSize={textSkinSizes[titleSize]}
                                help={help}
                            >
                                {title}
                            </TextGenericWithSkin>
                            {icon}
                            {filterAddon && <VStack className={addonClasses}>{filterAddon}</VStack>}
                        </HStack>
                    </VStack>
                </Cell>
            </StatefulTooltip>
        );
    }),
);
